// Utility function to convert blob URL to base64
export const blobUrlToBase64 = (blobUrl) => {
  return new Promise((resolve, reject) => {
    // Fetch the blob from the blob URL
    fetch(blobUrl)
      .then(response => response.blob())
      .then(blob => {
        // Create a FileReader to convert blob to base64
        const reader = new FileReader();
        
        reader.onload = () => {
          // reader.result contains the base64 string
          resolve(reader.result);
        };
        
        reader.onerror = () => {
          reject(new Error('Failed to convert blob to base64'));
        };
        
        // Convert blob to base64
        reader.readAsDataURL(blob);
      })
      .catch(error => {
        reject(error);
      });
  });
};

// Alternative function that takes a File object directly
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      resolve(reader.result);
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to convert file to base64'));
    };
    
    reader.readAsDataURL(file);
  });
};

const compressImageSource = (source, { maxWidth = 480, maxHeight = 480, quality = 0.72 } = {}) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to create canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for compression'));
    };

    img.src = source;
  });
};

export const fileToCompressedDataUrl = async (file, options = {}) => {
  const originalDataUrl = await fileToBase64(file);
  try {
    return await compressImageSource(originalDataUrl, options);
  } catch (error) {
    return originalDataUrl;
  }
};

export const blobUrlToCompressedDataUrl = async (blobUrl, options = {}) => {
  const originalDataUrl = await blobUrlToBase64(blobUrl);
  try {
    return await compressImageSource(originalDataUrl, options);
  } catch (error) {
    return originalDataUrl;
  }
};

// Function to convert multiple files at once
export const filesToBase64 = async (files) => {
  const promises = files.map(file => fileToBase64(file));
  return Promise.all(promises);
};

// ---------------------------------------------------------------------------
// Upload-time image compression
// ---------------------------------------------------------------------------
// Recompress large camera-roll photos before sending them over the wire.
// A modern phone takes 4-12 MB photos; for an event gallery viewed on phones
// 1920px @ q=0.82 looks indistinguishable and is typically 5-15x smaller.
//
// We intentionally produce a real `File` (not a data URL) so it streams as
// multipart/form-data instead of being base64-bloated by ~33%.

const DEFAULT_UPLOAD_COMPRESSION = {
  maxDimension: 1920,
  quality: 0.82,
  // If the source is already <= this many bytes AND fits within maxDimension,
  // we skip recompression to avoid pointless CPU work and possible quality loss.
  skipIfSmallerThanBytes: 600 * 1024, // 600 KB
};

const loadHtmlImageFromBlob = (blob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      // Hold the URL until the consumer is done; caller revokes after draw.
      resolve({ img, revoke: () => URL.revokeObjectURL(url) });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode image'));
    };
    img.src = url;
  });

const canvasToBlob = (canvas, mime, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob returned null'));
      },
      mime,
      quality
    );
  });

const replaceExtension = (name, newExt) => {
  if (!name) return `image.${newExt}`;
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base}.${newExt}`;
};

/**
 * Compress a single image file for upload. Always returns a File:
 *   - the recompressed JPEG if it is smaller than the original, OR
 *   - the original file if recompression failed / didn't help / file is small.
 *
 * Non-image inputs (e.g. videos) are returned unchanged.
 *
 * IMPORTANT: HEIC files from iOS Safari typically cannot be decoded via
 * <img>/canvas. In that case we silently fall back to the original file --
 * which is exactly what we want, because iOS Safari already converts HEIC
 * to JPEG when the user picks a photo from the camera roll into a file input
 * with `accept="image/*"`. The HEIC path here is a defense-in-depth fallback.
 */
export const compressImageFile = async (file, overrides = {}) => {
  if (!file || typeof file !== 'object') return file;
  if (!file.type || !file.type.startsWith('image/')) return file;

  const opts = { ...DEFAULT_UPLOAD_COMPRESSION, ...overrides };

  if (typeof file.size === 'number' && file.size <= opts.skipIfSmallerThanBytes) {
    return file;
  }

  let handle;
  try {
    handle = await loadHtmlImageFromBlob(file);
    const { img } = handle;

    const longest = Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height);
    if (!longest) return file;

    const scale = Math.min(opts.maxDimension / longest, 1);
    const targetW = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
    const targetH = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await canvasToBlob(canvas, 'image/jpeg', opts.quality);
    if (!blob || blob.size >= file.size) {
      return file;
    }

    return new File([blob], replaceExtension(file.name || 'image.jpg', 'jpg'), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (_) {
    return file;
  } finally {
    if (handle) handle.revoke();
  }
};

export const compressImageFiles = async (files, opts) => {
  if (!Array.isArray(files) || files.length === 0) return [];
  // Compress in parallel; canvas work is sync-but-fast and the awaits are
  // mostly image-decoder I/O so this still wins on multi-photo selections.
  return Promise.all(files.map((f) => compressImageFile(f, opts)));
};
